import { useState, useEffect } from "react";
import { useAuth, apiClient } from "../App";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Progress } from "../components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Palette, Wand2, ArrowLeft, CreditCard, Loader2, Sparkles, AlertCircle, Calendar, Copy, Library, Info } from "lucide-react";

const Generator = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  
  const [theme, setTheme] = useState("");
  const [ageGroup, setAgeGroup] = useState("kids");
  const [style, setStyle] = useState("cute");
  const [pageCount, setPageCount] = useState("1");
  const [variations, setVariations] = useState("1");
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [usage, setUsage] = useState(null);

  // Get max batch count based on user's plan
  const getMaxBatchCount = () => {
    const tier = user?.subscription_tier || "free";
    if (tier === "pro") return 5;
    if (tier === "plus") return 3;
    return 1;
  };

  const maxBatchCount = getMaxBatchCount();

  useEffect(() => {
    fetchUsage();
  }, []);

  const fetchUsage = async () => {
    try {
      const response = await apiClient.get("/usage");
      console.log("Usage data received:", response.data);
      setUsage(response.data);
      // Set default page count based on remaining daily pages
      const maxPages = Math.min(response.data.pages_remaining_today, response.data.pages_remaining_this_month, 10);
      if (maxPages > 0) {
        setPageCount(Math.min(maxPages, 5).toString());
      }
    } catch (error) {
      console.error("Error fetching usage:", error);
      console.error("Error response:", error.response?.data);
      // Show error toast to help debug
      if (error.response?.status === 401) {
        toast.error("Session expired. Please log in again.");
      } else if (error.response?.status === 403) {
        toast.error("Account issue: " + (error.response?.data?.detail || "Access denied"));
      }
    }
  };

  const pagesNeeded = parseInt(pageCount) * parseInt(variations);
  const canGenerate = usage && usage.pages_remaining_today >= pagesNeeded && usage.pages_remaining_this_month >= pagesNeeded;
  const maxAvailablePages = usage ? Math.min(usage.pages_remaining_today, usage.pages_remaining_this_month, 10) : 0;

  const themeExamples = [
    "Cute forest animals",
    "Space adventure with rockets",
    "Magical unicorns and rainbows",
    "Dinosaurs in jungle",
    "Indian mythology characters",
    "Underwater sea creatures",
    "Superhero adventures",
    "Princess in a castle"
  ];

  const handleGenerate = async () => {
    if (!theme.trim()) {
      toast.error("Please enter a theme for your coloring book");
      return;
    }

    if (!canGenerate) {
      toast.error("You've reached your daily or monthly limit");
      navigate("/pricing");
      return;
    }

    setGenerating(true);
    setProgress(0);

    try {
      // Step 1: Create the project(s) with batch support
      const createResponse = await apiClient.post(
        "/generate",
        { 
          theme, 
          age_group: ageGroup, 
          style, 
          page_count: parseInt(pageCount),
          variations: parseInt(variations)
        }
      );

      const projectId = createResponse.data.project_id;
      const projectIds = createResponse.data.project_ids || [projectId];
      const totalVariations = createResponse.data.variations || 1;
      
      toast.info(`Starting generation of ${pageCount} pages${totalVariations > 1 ? ` x ${totalVariations} variations` : ''}... This may take a few minutes.`);
      
      // Step 2: Generate pages with progress updates
      const progressInterval = setInterval(() => {
        setProgress(prev => Math.min(prev + 5, 90));
      }, 3000);

      // Generate pages for all projects in the batch
      try {
        for (const pid of projectIds) {
          await apiClient.post(
            `/projects/${pid}/generate-pages`,
            {},
            { timeout: 600000 }
          );
        }

        clearInterval(progressInterval);
        setProgress(100);
        
        await refreshUser();
        await fetchUsage();
        
        if (totalVariations > 1) {
          toast.success(`${totalVariations} variations created successfully!`);
          navigate("/library");
        } else {
          toast.success("Coloring book created successfully!");
          navigate(`/preview/${projectId}`);
        }
      } catch (pageError) {
        clearInterval(progressInterval);
        console.error("Page generation error:", pageError);
        
        try {
          const checkResponse = await apiClient.get(`/projects/${projectId}`);
          
          if (checkResponse.data.pages?.length > 0) {
            toast.warning("Some pages were generated. You can view your partial book.");
            navigate(`/preview/${projectId}`);
          } else {
            throw pageError;
          }
        } catch {
          toast.error("Failed to generate pages. Please try again.");
        }
      }
    } catch (error) {
      console.error("Generation error:", error);
      if (error.response?.status === 402) {
        toast.error(error.response?.data?.detail || "Daily or monthly limit reached.");
        navigate("/pricing");
      } else {
        toast.error(error.response?.data?.detail || "Failed to create coloring book.");
      }
    } finally {
      setGenerating(false);
    }
  };

  const dailyProgress = usage ? (usage.pages_used_today / usage.pages_limit_today) * 100 : 0;
  const monthlyProgress = usage ? (usage.pages_used_this_month / usage.pages_limit_this_month) * 100 : 0;

  return (
    <div className="min-h-screen bg-hero-gradient">
      {/* Header */}
      <header className="bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex items-center justify-between h-16">
            <Link to="/dashboard" className="flex items-center gap-2 text-slate-600 hover:text-slate-900" data-testid="back-to-dashboard">
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Back to Dashboard</span>
            </Link>

            {usage && (
              <div className="flex items-center gap-4">
                <div className="text-sm text-slate-600">
                  <span className="font-medium text-violet-600">{usage.pages_remaining_today}</span> pages left today
                </div>
                <div className="text-sm text-slate-600">
                  <span className="font-medium text-violet-600">{usage.pages_remaining_this_month}</span> this month
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12">
        {/* Limit Warning */}
        {usage && (usage.pages_remaining_today === 0 || usage.pages_remaining_this_month === 0) && (
          <div className="mb-8 p-6 bg-amber-50 border-2 border-amber-300 rounded-2xl">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                <AlertCircle className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <h3 className="font-semibold text-amber-800 text-lg mb-1" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                  {usage.pages_remaining_today === 0 
                    ? "Daily limit reached!" 
                    : "Monthly limit reached!"}
                </h3>
                <p className="text-amber-700 mb-3">
                  {usage.pages_remaining_today === 0 
                    ? "You've used all your pages for today. Try again tomorrow or upgrade your plan."
                    : `You've used all ${usage.pages_limit_this_month} pages this month. Resets on ${usage.reset_date}.`}
                </p>
                <Button
                  onClick={() => navigate("/pricing")}
                  className="h-10 px-6 rounded-full bg-amber-500 hover:bg-amber-600 text-white font-semibold"
                  data-testid="upgrade-cta"
                >
                  Upgrade Plan
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Usage Stats Mini */}
        {usage && (
          <div className="bg-white rounded-2xl p-4 border border-slate-100 mb-8">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-slate-600">Today</span>
                  <span className="text-xs text-slate-500">{usage.pages_used_today}/{usage.pages_limit_today}</span>
                </div>
                <Progress value={dailyProgress} className="h-1.5" />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-slate-600">This Month</span>
                  <span className="text-xs text-slate-500">{usage.pages_used_this_month}/{usage.pages_limit_this_month}</span>
                </div>
                <Progress value={monthlyProgress} className="h-1.5" />
              </div>
            </div>
          </div>
        )}

        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-600 to-indigo-600 mb-4">
            <Wand2 className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl font-bold text-slate-800 mb-3" style={{ fontFamily: "'Fredoka', sans-serif" }}>
            Create Your Coloring Book
          </h1>
          <p className="text-slate-600">
            Describe your theme and we'll create unique coloring pages
          </p>
        </div>

        <div className={`bg-white rounded-3xl p-8 shadow-lg border border-slate-100 ${!canGenerate ? 'opacity-60' : ''}`}>
          {/* Theme Input */}
          <div className="mb-6">
            <Label htmlFor="theme" className="text-base font-semibold text-slate-700 mb-2 block">
              What's your coloring book about?
            </Label>
            <Input
              id="theme"
              value={theme}
              onChange={(e) => setTheme(e.target.value)}
              placeholder="e.g., Cute animals playing in a forest"
              className="h-14 text-lg rounded-xl border-slate-200 bg-slate-50 focus:border-violet-500"
              disabled={generating || !canGenerate}
              data-testid="theme-input"
            />
            <div className="mt-3 flex flex-wrap gap-2">
              {themeExamples.slice(0, 4).map((example, i) => (
                <button
                  key={i}
                  onClick={() => setTheme(example)}
                  className="px-3 py-1.5 bg-violet-50 text-violet-700 rounded-full text-sm hover:bg-violet-100 transition-colors"
                  disabled={generating || !canGenerate}
                  data-testid={`theme-suggestion-${i}`}
                >
                  {example}
                </button>
              ))}
            </div>
          </div>

          {/* Age Group & Style */}
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div>
              <Label className="text-base font-semibold text-slate-700 mb-2 block">
                Age Group
              </Label>
              <Select value={ageGroup} onValueChange={setAgeGroup} disabled={generating || !canGenerate}>
                <SelectTrigger className="h-12 rounded-xl border-slate-200 bg-slate-50" data-testid="age-group-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="toddlers">Toddlers (2-4 years)</SelectItem>
                  <SelectItem value="kids">Kids (5-10 years)</SelectItem>
                  <SelectItem value="teens">Teens (11+ years)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-base font-semibold text-slate-700 mb-2 block">
                Art Style
              </Label>
              <Select value={style} onValueChange={setStyle} disabled={generating || !canGenerate}>
                <SelectTrigger className="h-12 rounded-xl border-slate-200 bg-slate-50" data-testid="style-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cute">Cute & Kawaii</SelectItem>
                  <SelectItem value="cartoon">Classic Cartoon</SelectItem>
                  <SelectItem value="realistic">Realistic</SelectItem>
                  <SelectItem value="fantasy">Fantasy & Magical</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Number of Pages */}
          <div className="mb-6">
            <Label className="text-base font-semibold text-slate-700 mb-2 block">
              Number of Pages
            </Label>
            <Select 
              value={pageCount} 
              onValueChange={setPageCount} 
              disabled={generating || !canGenerate}
            >
              <SelectTrigger className="h-12 rounded-xl border-slate-200 bg-slate-50" data-testid="page-count-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                  <SelectItem 
                    key={num} 
                    value={num.toString()}
                    disabled={num * parseInt(variations) > maxAvailablePages}
                  >
                    {num} {num === 1 ? 'page' : 'pages'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Batch Generation (Variations) */}
          {maxBatchCount > 1 && (
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-2">
                <Label className="text-base font-semibold text-slate-700">
                  Generate Variations
                </Label>
                <div className="group relative">
                  <Info className="w-4 h-4 text-slate-400 cursor-help" />
                  <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 bg-slate-900 text-white text-xs rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
                    Generate multiple variations of the same prompt at once. Each variation will be a unique interpretation of your theme.
                    <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-900"></div>
                  </div>
                </div>
                <span className="ml-auto text-xs bg-violet-100 text-violet-700 px-2 py-1 rounded-full font-medium">
                  {user?.subscription_tier === "pro" ? "Pro" : "Plus"} Feature
                </span>
              </div>
              <Select 
                value={variations} 
                onValueChange={setVariations} 
                disabled={generating || !canGenerate}
              >
                <SelectTrigger className="h-12 rounded-xl border-slate-200 bg-slate-50" data-testid="variations-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: maxBatchCount }, (_, i) => i + 1).map((num) => (
                    <SelectItem 
                      key={num} 
                      value={num.toString()}
                      disabled={parseInt(pageCount) * num > maxAvailablePages}
                    >
                      {num === 1 ? '1 variation (standard)' : `${num} variations`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-sm text-slate-500 mt-2">
                <Copy className="w-4 h-4 inline mr-1" />
                Create {variations} unique {parseInt(variations) === 1 ? 'version' : 'versions'} of your coloring book
              </p>
            </div>
          )}

          {/* Cost Summary */}
          <div className="bg-slate-50 rounded-xl p-4 mb-8 border border-slate-200">
            <div className="flex items-center justify-between">
              <span className="text-slate-600">Pages per book:</span>
              <span className="font-semibold">{pageCount}</span>
            </div>
            {parseInt(variations) > 1 && (
              <div className="flex items-center justify-between mt-2">
                <span className="text-slate-600">Variations:</span>
                <span className="font-semibold">×{variations}</span>
              </div>
            )}
            <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-200">
              <span className="text-slate-700 font-medium">Total pages used:</span>
              <span className="font-bold text-violet-600">{pagesNeeded} pages</span>
            </div>
          </div>
          
          <p className="text-sm text-slate-500 mb-6">
            You can generate up to <span className="font-semibold text-violet-600">{maxAvailablePages}</span> pages right now.
          </p>

          {/* Generation Progress */}
          {generating && (
            <div className="mb-8 p-6 bg-violet-50 rounded-2xl">
              <div className="flex items-center gap-3 mb-4">
                <Loader2 className="w-5 h-5 text-violet-600 animate-spin" />
                <span className="font-medium text-violet-700">
                  Creating your coloring book...
                </span>
              </div>
              <Progress value={progress} className="h-2 mb-2" />
              <p className="text-sm text-violet-600">
                Generating {pageCount} unique pages... This may take a few minutes.
              </p>
            </div>
          )}

          {/* Generate Button */}
          <Button
            onClick={handleGenerate}
            disabled={generating || !theme.trim() || !canGenerate}
            className="w-full h-14 rounded-full bg-gradient-to-r from-violet-600 to-indigo-600 text-white font-semibold text-lg hover:shadow-xl btn-lift disabled:opacity-50"
            data-testid="generate-btn"
          >
            {generating ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Generating {pageCount} pages...
              </>
            ) : (
              <>
                <Sparkles className="w-5 h-5 mr-2" />
                Generate {pageCount} {parseInt(pageCount) === 1 ? 'Page' : 'Pages'}
              </>
            )}
          </Button>

          <p className="text-center text-sm text-slate-500 mt-4">
            {usage?.quality === "basic" && "Free plan: Basic quality with watermarks"}
            {usage?.quality === "standard" && "Basic plan: Standard quality PDFs"}
            {usage?.quality === "high" && "Plus plan: High quality, commercial license"}
            {usage?.quality === "premium" && "Pro plan: Premium quality, full features"}
          </p>
        </div>

        {/* Tips */}
        <div className="mt-8 p-6 bg-white/50 rounded-2xl border border-slate-100">
          <h3 className="font-semibold text-slate-800 mb-3" style={{ fontFamily: "'Fredoka', sans-serif" }}>
            Tips for great coloring books:
          </h3>
          <ul className="space-y-2 text-slate-600 text-sm">
            <li>• Be specific about characters and settings</li>
            <li>• Include action words like "playing", "exploring", "dancing"</li>
            <li>• Mention specific animals, objects, or themes</li>
            <li>• Choose the right age group for complexity</li>
          </ul>
        </div>
      </main>
    </div>
  );
};

export default Generator;
